import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Pagination from '../components/Pagination';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import './SetsPage.css';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

interface SetMetrics {
  setCode:            string;
  name:               string;
  setType:            string;
  releasedAt:         string | null;
  cardCount:          number;
  avgPrice:           number | null;
  totalSetValue:      number | null;
  cardsOwned:         number;
  valueOwned:         number | null;
  cheapestName:          string | null;
  cheapestCardId:        string | null;
  cheapestImage:         string | null;
  cheapestPrice:         number | null;
  mostExpensiveName:     string | null;
  mostExpensiveCardId:   string | null;
  mostExpensiveImage:    string | null;
  mostExpensivePrice:    number | null;
}

interface SetOption {
  setCode:    string;
  name:       string;
  setType:    string;
  releasedAt: string | null;
}

interface SetFilters {
  setTypes:      string[];
  setCodes:      string[];
  cardCountMin:  number;
  cardCountMax:  number;
  avgPriceMin:   number;
  avgPriceMax:   number;
  setValueMin:   number;
  setValueMax:   number;
  releaseYearFrom: number;
  releaseYearTo:   number;
  ownedOnly:     boolean;
}

const DEFAULT_FILTERS: SetFilters = {
  setTypes: [], setCodes: [],
  cardCountMin: 0, cardCountMax: 0,
  avgPriceMin: 0, avgPriceMax: 0,
  setValueMin: 0, setValueMax: 0,
  releaseYearFrom: 0, releaseYearTo: 0,
  ownedOnly: false,
};

type SortKey =
  | 'released_desc' | 'released_asc'
  | 'name' | 'name_desc'
  | 'card_count' | 'avg_price' | 'total_value'
  | 'most_expensive' | 'owned_count' | 'owned_value';

interface ColDef { key: SortKey | null; label: string; align: 'left'|'right'|'center'; }

const COLUMNS: ColDef[] = [
  { key: 'name',          label: 'Set Name',        align: 'left'   },
  { key: null,            label: 'Code',            align: 'center' },
  { key: 'card_count',    label: 'Cards',           align: 'right'  },
  { key: 'released_desc', label: 'Released',        align: 'center' },
  { key: null,            label: 'Type',            align: 'center' },
  { key: 'total_value',   label: 'Set Value',       align: 'right'  },
  { key: 'avg_price',     label: 'Avg Price',       align: 'right'  },
  { key: null,            label: 'Cheapest',        align: 'left'   },
  { key: 'most_expensive',label: 'Most Expensive',  align: 'left'   },
  { key: 'owned_count',   label: 'Owned',           align: 'right'  },
  { key: 'owned_value',   label: 'Owned Value',     align: 'right'  },
];

const SET_TYPE_OPTIONS = [
  { value: 'expansion',        label: 'Expansion'       },
  { value: 'core',             label: 'Core'            },
  { value: 'masters',          label: 'Masters'         },
  { value: 'draft_innovation', label: 'Draft'           },
  { value: 'commander',        label: 'Commander'       },
  { value: 'duel_deck',        label: 'Duel Deck'       },
  { value: 'starter',          label: 'Starter'         },
  { value: 'planechase',       label: 'Planechase'      },
  { value: 'archenemy',        label: 'Archenemy'       },
  { value: 'from_the_vault',   label: 'From the Vault'  },
  { value: 'masterpiece',      label: 'Masterpiece'     },
  { value: 'promo',            label: 'Promo'           },
  { value: 'box',              label: 'Box'             },
  { value: 'premium_deck',     label: 'Premium Deck'    },
  { value: 'spellbook',        label: 'Spellbook'       },
];

const SET_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  SET_TYPE_OPTIONS.map(o => [o.value, o.label])
);

function fmt(v: number | null, decimals = 2): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toFixed(decimals)}`;
}
function fmtK(v: number | null): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${Number(v).toFixed(0)}`;
}

function hasFilters(f: SetFilters): boolean {
  return f.setTypes.length > 0 || f.setCodes.length > 0 ||
    f.cardCountMin > 0 || f.cardCountMax > 0 ||
    f.avgPriceMin > 0 || f.avgPriceMax > 0 ||
    f.setValueMin > 0 || f.setValueMax > 0 ||
    f.releaseYearFrom > 0 || f.releaseYearTo > 0 ||
    f.ownedOnly;
}

// ── Card image hover popup ────────────────────────────────────────────────
const IMAGE_BASE = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api/images`;

function CardHoverPopup({ name, imageUrl, price, x, y }: {
  name: string; imageUrl: string | null; price: number | null; x: number; y: number;
}) {
  const showLeft = x > window.innerWidth * 0.6;
  const popupW = 280;
  const top = Math.min(Math.max(y - 80, 10), window.innerHeight - Math.round(popupW * 1.4) - 10);

  if (!imageUrl) return null;

  // local_normal is already a relative path e.g. en/phpr/normal/xxx.jpg
  const src = imageUrl.startsWith('http')
    ? imageUrl
    : `${IMAGE_BASE}/${imageUrl}`;

  return (
    <div className="set-card-popup" style={{
      position: 'fixed', top, zIndex: 9999, pointerEvents: 'none',
      left: showLeft ? x - popupW - 16 : x + 16,
    }}>
      <img src={src} alt={name} className="set-card-popup-img" />
      {price !== null && <div className="set-card-popup-price">{fmt(price)}</div>}
    </div>
  );
}

// ── Filter Drawer ─────────────────────────────────────────────────────────
interface DrawerProps {
  filters:  SetFilters;
  allSets:  SetOption[];
  open:     boolean;
  pinned:   boolean;
  onClose:     () => void;
  onTogglePin: () => void;
  onChange:    (f: SetFilters) => void;
}

function SetFilterDrawer({ filters, allSets, open, pinned, onClose, onTogglePin, onChange }: DrawerProps) {
  const [setSearch,   setSetSearch]   = useState('');
  const [setDropOpen, setSetDropOpen] = useState(false);
  const setWrapRef  = useRef<HTMLDivElement>(null);
  const setInputRef = useRef<HTMLInputElement | null>(null);
  const cancelCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => { if (cancelCloseRef.current) clearTimeout(cancelCloseRef.current); };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (setWrapRef.current && !setWrapRef.current.contains(e.target as Node))
        setSetDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const reset = () => onChange({ ...DEFAULT_FILTERS });

  const toggleType = (v: string) => {
    const types = filters.setTypes.includes(v)
      ? filters.setTypes.filter(t => t !== v)
      : [...filters.setTypes, v];
    onChange({ ...filters, setTypes: types });
  };

  const addSetCode = (code: string, ref?: React.RefObject<HTMLInputElement | null>) => {
    const c = code.trim().toLowerCase();
    if (!c || filters.setCodes.includes(c)) return;
    onChange({ ...filters, setCodes: [...filters.setCodes, c] });
    setSetSearch('');
    setSetDropOpen(true);
    setTimeout(() => ref?.current?.focus(), 0);
  };

  const removeSetCode = (code: string) =>
    onChange({ ...filters, setCodes: filters.setCodes.filter(c => c !== code) });

  // Filtered set list — same logic as FilterDrawer
  const filteredSets = (() => {
    const q = setSearch.trim().toLowerCase();
    if (!q) return allSets.slice(0, 150);
    const qLoose = q.replace(/[^a-z0-9]/g, '');
    return allSets.filter(s => {
      const name = s.name.toLowerCase();
      const code = s.setCode.toLowerCase();
      if (name.includes(q) || code.includes(q)) return true;
      if (name.replace(/[^a-z0-9]/g, '').includes(qLoose) ||
          code.replace(/[^a-z0-9]/g, '').includes(qLoose)) return true;
      return false;
    });
  })();

  return (
    <>
      <div className={`sets-drawer ${open ? 'open' : ''} ${pinned ? 'pinned' : ''}`}>
        {/* Header */}
        <div className="sets-drawer-header">
          <div className="sets-drawer-title">
            <span className="sets-drawer-title-text">Filters</span>
            {hasFilters(filters) && (
              <button className="sets-drawer-reset" onClick={reset}>Reset all</button>
            )}
          </div>
          <div className="sets-drawer-actions">
            <button className={`sets-pin-btn ${pinned ? 'active' : ''}`} onClick={onTogglePin} title={pinned ? 'Unpin' : 'Pin open'}>📌</button>
            {!pinned && <button className="sets-close-btn" onClick={onClose}>✕</button>}
          </div>
        </div>

        <div className="sets-drawer-body">

          {/* Owned Only */}
          <div className="sets-drawer-section">
            <label className="sets-toggle">
              <input type="checkbox" checked={filters.ownedOnly}
                onChange={e => onChange({ ...filters, ownedOnly: e.target.checked })} />
              <span className="sets-toggle-track"><span className="sets-toggle-thumb" /></span>
              <span className="sets-toggle-label">Owned Sets Only</span>
            </label>
          </div>

          {/* Release Year */}
          <div className="sets-drawer-section">
            <div className="sets-section-label">Release Year</div>
            <div className="sets-range-row">
              <input type="number" className="sets-range-input" placeholder="From"
                min={1993} max={2099} value={filters.releaseYearFrom || ''}
                onChange={e => onChange({ ...filters, releaseYearFrom: Number(e.target.value) || 0 })} />
              <span className="sets-range-sep">–</span>
              <input type="number" className="sets-range-input" placeholder="To"
                min={1993} max={2099} value={filters.releaseYearTo || ''}
                onChange={e => onChange({ ...filters, releaseYearTo: Number(e.target.value) || 0 })} />
            </div>
          </div>

          {/* Set Type */}
          <div className="sets-drawer-section">
            <div className="sets-section-label">Set Type</div>
            <div className="sets-type-grid">
              {SET_TYPE_OPTIONS.map(t => (
                <button key={t.value}
                  className={`sets-type-pill ${filters.setTypes.includes(t.value) ? 'active' : ''}`}
                  onClick={() => toggleType(t.value)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Set — searchable dropdown */}
          <div className="sets-drawer-section">
            <div className="sets-section-label">Set</div>
            {filters.setCodes.length > 0 && (
              <div className="sets-active-codes">
                {filters.setCodes.map(c => {
                  const info = allSets.find(s => s.setCode === c);
                  return (
                    <span key={c} className="sets-code-badge">
                      <span className="sets-code-badge-code">{c.toUpperCase()}</span>
                      <span className="sets-code-badge-name">{info?.name || c.toUpperCase()}</span>
                      <button onClick={() => removeSetCode(c)}>✕</button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="sets-set-search-wrap" ref={setWrapRef}>
              <input
                ref={setInputRef}
                className="sets-range-input sets-set-search-input"
                type="text"
                placeholder="Search sets…"
                value={setSearch}
                onChange={e => { setSetSearch(e.target.value); setSetDropOpen(true); }}
                onFocus={() => setSetDropOpen(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && filteredSets.length > 0) {
                    e.preventDefault();
                    cancelClose();
                    addSetCode(filteredSets[0].setCode, { current: setInputRef.current });
                  }
                  if (e.key === 'Escape') { setSetDropOpen(false); setSetSearch(''); }
                }}
              />
              {setDropOpen && filteredSets.length > 0 && (
                <div className="sets-set-dropdown" onMouseDown={e => e.stopPropagation()}>
                  {filteredSets.map(s => (
                    <button
                      key={s.setCode}
                      className={`sets-set-option ${filters.setCodes.includes(s.setCode) ? 'active' : ''}`}
                      onMouseDown={e => {
                        e.preventDefault();
                        cancelClose();
                        addSetCode(s.setCode, { current: setInputRef.current });
                      }}
                    >
                      <span className="sets-set-option-code">{s.setCode.toUpperCase()}</span>
                      <span className="sets-set-option-name">{s.name}</span>
                      <span className="sets-set-option-year">
                        {s.releasedAt ? new Date(s.releasedAt).getFullYear() : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Card Count */}
          <div className="sets-drawer-section">
            <div className="sets-section-label">Card Count</div>
            <div className="sets-range-row">
              <input type="number" className="sets-range-input" placeholder="Min"
                min={0} value={filters.cardCountMin || ''}
                onChange={e => onChange({ ...filters, cardCountMin: Number(e.target.value) || 0 })} />
              <span className="sets-range-sep">–</span>
              <input type="number" className="sets-range-input" placeholder="Max"
                min={0} value={filters.cardCountMax || ''}
                onChange={e => onChange({ ...filters, cardCountMax: Number(e.target.value) || 0 })} />
            </div>
          </div>

          {/* Avg Price */}
          <div className="sets-drawer-section">
            <div className="sets-section-label">Avg Price (USD)</div>
            <div className="sets-range-row">
              <input type="number" className="sets-range-input" placeholder="Min"
                min={0} value={filters.avgPriceMin || ''}
                onChange={e => onChange({ ...filters, avgPriceMin: Number(e.target.value) || 0 })} />
              <span className="sets-range-sep">–</span>
              <input type="number" className="sets-range-input" placeholder="Max"
                min={0} value={filters.avgPriceMax || ''}
                onChange={e => onChange({ ...filters, avgPriceMax: Number(e.target.value) || 0 })} />
            </div>
          </div>

          {/* Set Value */}
          <div className="sets-drawer-section" style={{ borderBottom: 'none' }}>
            <div className="sets-section-label">Set Value (USD)</div>
            <div className="sets-range-row">
              <input type="number" className="sets-range-input" placeholder="Min"
                min={0} value={filters.setValueMin || ''}
                onChange={e => onChange({ ...filters, setValueMin: Number(e.target.value) || 0 })} />
              <span className="sets-range-sep">–</span>
              <input type="number" className="sets-range-input" placeholder="Max"
                min={0} value={filters.setValueMax || ''}
                onChange={e => onChange({ ...filters, setValueMax: Number(e.target.value) || 0 })} />
            </div>
          </div>

        </div>
      </div>
      {open && !pinned && (
        <div className="sets-drawer-overlay" onClick={onClose} />
      )}
    </>
  );
}

// ── Active filter badges ──────────────────────────────────────────────────
function ActiveFilterBadges({ filters, onChange }: { filters: SetFilters; onChange: (f: SetFilters) => void }) {
  const badges: { label: string; onRemove: () => void }[] = [];

  if (filters.ownedOnly)
    badges.push({ label: 'Owned Only', onRemove: () => onChange({ ...filters, ownedOnly: false }) });
  if (filters.releaseYearFrom > 0 || filters.releaseYearTo > 0)
    badges.push({ label: `Year: ${filters.releaseYearFrom || '?'} – ${filters.releaseYearTo || '?'}`,
      onRemove: () => onChange({ ...filters, releaseYearFrom: 0, releaseYearTo: 0 }) });
  filters.setTypes.forEach(t =>
    badges.push({ label: SET_TYPE_LABELS[t] || t,
      onRemove: () => onChange({ ...filters, setTypes: filters.setTypes.filter(x => x !== t) }) }));
  filters.setCodes.forEach(c =>
    badges.push({ label: c.toUpperCase(),
      onRemove: () => onChange({ ...filters, setCodes: filters.setCodes.filter(x => x !== c) }) }));
  if (filters.cardCountMin > 0 || filters.cardCountMax > 0)
    badges.push({ label: `Cards: ${filters.cardCountMin || '0'} – ${filters.cardCountMax || '∞'}`,
      onRemove: () => onChange({ ...filters, cardCountMin: 0, cardCountMax: 0 }) });
  if (filters.avgPriceMin > 0 || filters.avgPriceMax > 0)
    badges.push({ label: `Avg: $${filters.avgPriceMin || '0'} – $${filters.avgPriceMax || '∞'}`,
      onRemove: () => onChange({ ...filters, avgPriceMin: 0, avgPriceMax: 0 }) });
  if (filters.setValueMin > 0 || filters.setValueMax > 0)
    badges.push({ label: `Value: $${filters.setValueMin || '0'} – $${filters.setValueMax || '∞'}`,
      onRemove: () => onChange({ ...filters, setValueMin: 0, setValueMax: 0 }) });

  if (!badges.length) return null;
  return (
    <div className="sets-active-badges">
      {badges.map((b, i) => (
        <span key={i} className="sets-active-badge">
          {b.label}<button onClick={b.onRemove}>✕</button>
        </span>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function SetsPage() {
  const navigate = useNavigate();
  const [sets,       setSets]       = useState<SetMetrics[]>([]);
  const [allSets,    setAllSets]    = useState<SetOption[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [query,      setQuery]      = useState('');
  const [sortBy,     setSortBy]     = useState<SortKey>('released_desc');
  const [page,       setPage]       = useState(0);
  const [limit,      setLimit]      = useState(48);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters,    setFilters]    = useState<SetFilters>(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pinned,     setPinned]     = useState(() => {
    try { return localStorage.getItem('setFilterPinned') === 'true'; } catch { return false; }
  });
  const [hoverCard, setHoverCard] = useState<{ name: string; imageUrl: string | null; price: number | null; x: number; y: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number; items: ContextMenuItem[];
  } | null>(null);

  const goToCardsForSet = (setCode: string) => {
    navigate(`/cards?setCodes=${encodeURIComponent(setCode)}`, { state: { from: '/sets' } });
  };

  const handleSetContextMenu = (setCode: string, setName: string, e: React.MouseEvent) => {
    e.preventDefault();
    setHoverCard(null);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: '🔍',
          label: `View Cards in ${setName}`,
          onClick: () => goToCardsForSet(setCode),
        },
      ],
    });
  };

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef    = useRef<AbortController | null>(null);

  useEffect(() => {
    axios.get(`${API}/sets`).then(res => setAllSets(res.data)).catch(() => {});
  }, []);

  const fetchSets = useCallback(async (
    q: string, sort: SortKey, pg: number, lim: number, f: SetFilters
  ) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sets/metrics`, {
        params: {
          q: q.trim(),
          sortBy: sort,
          page: pg,
          limit: lim,
          setTypes:       f.setTypes.join(','),
          setCodes:       f.setCodes.join(','),
          cardCountMin:   f.cardCountMin,
          cardCountMax:   f.cardCountMax,
          avgPriceMin:    f.avgPriceMin,
          avgPriceMax:    f.avgPriceMax,
          setValueMin:    f.setValueMin,
          setValueMax:    f.setValueMax,
          releaseYearFrom:f.releaseYearFrom,
          releaseYearTo:  f.releaseYearTo,
          ownedOnly:      f.ownedOnly,
        },
        signal,
      });
      if (!signal.aborted) {
        setSets(res.data.sets);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      }
    } catch (e: any) {
      if (e?.code !== 'ERR_CANCELED') console.error('Sets fetch failed', e);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSets(query, sortBy, page, limit, filters);
    }, query ? 300 : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, sortBy, page, limit, filters, fetchSets]);

  const handleColClick = (col: SortKey | null) => {
    if (!col) return;
    let next: SortKey = col;
    if (col === 'name'          && sortBy === 'name')         next = 'name_desc';
    if (col === 'name_desc'     && sortBy === 'name_desc')    next = 'name';
    if (col === 'released_desc' && sortBy === 'released_desc')next = 'released_asc';
    if (col === 'released_asc'  && sortBy === 'released_asc') next = 'released_desc';
    setSortBy(next);
    setPage(0);
  };

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    try { localStorage.setItem('setFilterPinned', String(next)); } catch {}
    if (next) setDrawerOpen(true);
    else setDrawerOpen(false);
  };

  const sortActive = (col: SortKey | null) =>
    col !== null && (sortBy === col ||
      (col === 'name' && sortBy === 'name_desc') ||
      (col === 'released_desc' && sortBy === 'released_asc'));

  const sortIcon = (col: SortKey | null) => {
    if (!col || !sortActive(col)) return <span className="sort-icon inactive">⇅</span>;
    const desc = ['released_desc','name_desc','card_count','avg_price','total_value','most_expensive','owned_count','owned_value'].includes(sortBy);
    return <span className="sort-icon active">{desc ? '↓' : '↑'}</span>;
  };

  const activeFilters = hasFilters(filters);

  return (
    <div className={`sets-page ${pinned ? 'drawer-pinned' : ''}`}>
      <SetFilterDrawer
        filters={filters}
        allSets={allSets}
        open={drawerOpen || pinned}
        pinned={pinned}
        onClose={() => setDrawerOpen(false)}
        onTogglePin={togglePin}
        onChange={f => { setFilters(f); setPage(0); }}
      />

      {/* Filter trigger */}
      {!pinned && (
        <div className={`sets-filter-trigger ${drawerOpen ? 'hidden' : ''}`}
          onClick={() => setDrawerOpen(true)} title="Open filters">
          <span className="trigger-icon">⚙</span>
          {activeFilters && <span className="trigger-dot" />}
        </div>
      )}

      <div className="sets-content">
        {/* Header */}
        <div className="sets-header">
          <h1 className="sets-title">Sets</h1>
          <span className="sets-total-count">
            <strong>{total.toLocaleString()}</strong> sets
          </span>
          <input
            className="sets-search"
            type="text"
            placeholder="Quick search…"
            value={query}
            onChange={e => { setQuery(e.target.value); setPage(0); }}
          />
          {total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={p => setPage(p)}
              onLimitChange={l => { setLimit(l); setPage(0); }}
              compact
            />
          )}
        </div>

        {/* Active filter badges */}
        <ActiveFilterBadges filters={filters} onChange={f => { setFilters(f); setPage(0); }} />

        {/* Table */}
        <div className="sets-table-wrap">
          {loading && <div className="sets-loading"><div className="loading-spinner" /></div>}
          <table className="sets-table">
            <thead>
              <tr>
                {COLUMNS.map(col => (
                  <th key={col.label}
                    className={`sets-th align-${col.align} ${col.key ? 'sortable' : ''} ${sortActive(col.key) ? 'sort-active' : ''}`}
                    onClick={() => handleColClick(col.key)}>
                    {col.label}{sortIcon(col.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sets.length === 0 && !loading ? (
                <tr><td colSpan={COLUMNS.length} className="sets-empty">No sets found</td></tr>
              ) : (
                sets.map(s => (
                  <tr key={s.setCode} className="sets-row">
                    <td className="align-left"
                      onContextMenu={e => handleSetContextMenu(s.setCode, s.name, e)}>
                      <span className="set-full-name">{s.name}</span>
                    </td>
                    <td className="align-center"
                      onContextMenu={e => handleSetContextMenu(s.setCode, s.name, e)}>
                      <span className="set-code-pill">{s.setCode.toUpperCase()}</span>
                    </td>
                    <td className="align-right sets-num">{s.cardCount.toLocaleString()}</td>
                    <td className="align-center sets-released">
                      {s.releasedAt ? s.releasedAt.slice(0, 7) : '—'}
                    </td>
                    <td className="align-center">
                      <span className="set-type-pill">
                        {SET_TYPE_LABELS[s.setType] || s.setType}
                      </span>
                    </td>
                    <td className="align-right sets-num sets-highlight">{fmtK(s.totalSetValue)}</td>
                    <td className="align-right sets-num">{fmt(s.avgPrice)}</td>
                    <td className="align-left sets-card-name">
                      {s.cheapestName ? (
                        <span
                          onMouseEnter={e => setHoverCard({ name: s.cheapestName!, imageUrl: s.cheapestImage, price: s.cheapestPrice, x: e.clientX, y: e.clientY })}
                          onMouseMove={e  => setHoverCard(h => h ? { ...h, x: e.clientX, y: e.clientY } : null)}
                          onMouseLeave={() => setHoverCard(null)}
                          className="sets-card-hover-name">
                          <span className="sets-card-price-inline">{fmt(s.cheapestPrice)}</span>
                          {s.cheapestName}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="align-left sets-card-name">
                      {s.mostExpensiveName ? (
                        <span
                          onMouseEnter={e => setHoverCard({ name: s.mostExpensiveName!, imageUrl: s.mostExpensiveImage, price: s.mostExpensivePrice, x: e.clientX, y: e.clientY })}
                          onMouseMove={e  => setHoverCard(h => h ? { ...h, x: e.clientX, y: e.clientY } : null)}
                          onMouseLeave={() => setHoverCard(null)}
                          className="sets-card-hover-name">
                          <span className="sets-card-price-inline">{fmt(s.mostExpensivePrice)}</span>
                          {s.mostExpensiveName}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="align-right sets-num">
                      {s.cardsOwned > 0
                        ? <span className="sets-owned-count">×{s.cardsOwned}</span>
                        : '—'}
                    </td>
                    <td className="align-right sets-num sets-owned-value">
                      {s.valueOwned && s.valueOwned > 0 ? fmt(s.valueOwned) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="sets-footer">
            <Pagination
              page={page} totalPages={totalPages} total={total} limit={limit}
              onPageChange={p => setPage(p)}
              onLimitChange={l => { setLimit(l); setPage(0); }}
            />
          </div>
        )}
      </div>

      {/* Card hover popup */}
      {hoverCard && (
        <CardHoverPopup
          name={hoverCard.name}
          imageUrl={hoverCard.imageUrl}
          price={hoverCard.price}
          x={hoverCard.x}
          y={hoverCard.y}
        />
      )}

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
